/**
 * Template Icon and Color Mapper
 * Maps template IDs/categories to appropriate icons and colors
 */

import React from 'react';
import {
  Calendar,
  FileCheck,
  Droplet,
  FileText,
  AlertTriangle,
  MapPin,
  CheckCircle,
  TrendingUp,
  Database,
  ClipboardCheck,
  Shield,
  Activity,
  Clock,
} from 'lucide-react';
import { TemplateIconColorMapping } from '../types/reports';

/**
 * Get icon for template based on ID, category, or name
 */
export function getIconForTemplate(
  id: string,
  category?: string,
  name?: string
): React.ComponentType<{ className?: string }> {
  const idLower = id.toLowerCase();
  const nameLower = (name || '').toLowerCase();
  const categoryLower = (category || '').toLowerCase();

  // Quality/Validation
  if (idLower.includes('qa') || idLower.includes('qc') || idLower.includes('validation')) {
    return ClipboardCheck;
  }
  
  // Efficiency/Processing
  if (idLower.includes('efficiency') || idLower.includes('processing') || idLower.includes('cost')) {
    return TrendingUp;
  }
  
  // Data/Export
  if (idLower.includes('vertical') || idLower.includes('conversion') || idLower.includes('export')) {
    return Database;
  }
  
  // Water/Redox
  if (idLower.includes('tea') || idLower.includes('redox') || idLower.includes('water')) {
    return Droplet;
  }
  
  // Monthly
  if (idLower.includes('monthly')) {
    return Calendar;
  }
  
  // Quarterly
  if (idLower.includes('quarterly') || idLower.includes('assessment')) {
    return FileCheck;
  }
  
  // Spatial/Mapping
  if (idLower.includes('spatial') || idLower.includes('distribution') || idLower.includes('mapping')) {
    return MapPin;
  }
  
  // Annual/Summary
  if (idLower.includes('annual') || idLower.includes('summary')) {
    return FileText;
  }
  
  // Incident/Exceedance
  if (idLower.includes('incident') || idLower.includes('exceedance') || idLower.includes('alert')) {
    return AlertTriangle;
  }
  
  // Compliance/CTO
  if (idLower.includes('cto') || idLower.includes('compliance') || idLower.includes('fc')) {
    return Shield;
  }
  
  // Activity/Monitoring
  if (idLower.includes('monitoring') || idLower.includes('caaqms') || idLower.includes('activity')) {
    return Activity;
  }
  
  // Form/Production
  if (idLower.includes('form') || idLower.includes('production')) {
    return FileText;
  }
  
  // Default based on category
  if (categoryLower.includes('quality') || categoryLower.includes('assurance')) {
    return ClipboardCheck;
  }
  if (categoryLower.includes('compliance')) {
    return FileCheck;
  }
  if (categoryLower.includes('technical')) {
    return MapPin;
  }
  if (categoryLower.includes('incident')) {
    return AlertTriangle;
  }
  
  return FileText;
}

/**
 * Get color gradient for template based on ID, category, or name
 */
export function getColorForTemplate(id: string, category?: string): string {
  const idLower = id.toLowerCase();
  const categoryLower = (category || '').toLowerCase();

  // Quality/Validation - Green
  if (idLower.includes('qa') || idLower.includes('qc') || idLower.includes('validation') || idLower.includes('fc_compliance')) {
    return 'from-green-500 to-green-600';
  }
  
  // Efficiency/Processing - Blue
  if (idLower.includes('efficiency') || idLower.includes('processing') || idLower.includes('cost')) {
    return 'from-blue-500 to-blue-600';
  }
  
  // Data/Export - Indigo
  if (idLower.includes('vertical') || idLower.includes('conversion') || idLower.includes('export')) {
    return 'from-indigo-500 to-indigo-600';
  }
  
  // Water/Redox - Cyan
  if (idLower.includes('tea') || idLower.includes('redox') || idLower.includes('water')) {
    return 'from-cyan-500 to-cyan-600';
  }
  
  // Monthly - Blue/Emerald
  if (idLower.includes('monthly')) {
    if (idLower.includes('ec')) {
      return 'from-emerald-500 to-emerald-600';
    }
    if (idLower.includes('caaqms')) {
      return 'from-indigo-500 to-indigo-600';
    }
    return 'from-blue-500 to-blue-600';
  }
  
  // Quarterly - Teal/Orange
  if (idLower.includes('quarterly')) {
    if (idLower.includes('cto')) {
      return 'from-orange-500 to-orange-600';
    }
    return 'from-teal-500 to-teal-600';
  }
  
  // Assessment - Teal
  if (idLower.includes('assessment')) {
    return 'from-teal-500 to-teal-600';
  }
  
  // Spatial/Distribution - Purple
  if (idLower.includes('spatial') || idLower.includes('distribution')) {
    return 'from-purple-500 to-purple-600';
  }
  
  // Annual - Green/Purple
  if (idLower.includes('annual')) {
    if (idLower.includes('environmental')) {
      return 'from-purple-500 to-purple-600';
    }
    return 'from-green-500 to-green-600';
  }
  
  // Summary - Green
  if (idLower.includes('summary')) {
    return 'from-green-500 to-green-600';
  }
  
  // Incident/Exceedance - Red
  if (idLower.includes('incident') || idLower.includes('exceedance') || idLower.includes('alert')) {
    return 'from-red-500 to-red-600';
  }
  
  // Six-Monthly - Teal
  if (idLower.includes('six_monthly') || idLower.includes('six-monthly')) {
    return 'from-teal-500 to-teal-600';
  }
  
  // Form M - Amber
  if (idLower.includes('form_m') || idLower.includes('form-m')) {
    return 'from-amber-500 to-amber-600';
  }
  
  // Default based on category
  if (categoryLower.includes('quality') || categoryLower.includes('assurance')) {
    return 'from-green-500 to-green-600';
  }
  if (categoryLower.includes('compliance')) {
    return 'from-teal-500 to-teal-600';
  }
  if (categoryLower.includes('technical')) {
    return 'from-purple-500 to-purple-600';
  }
  if (categoryLower.includes('incident')) {
    return 'from-red-500 to-red-600';
  }
  if (categoryLower.includes('project') || categoryLower.includes('management')) {
    return 'from-blue-500 to-blue-600';
  }
  if (categoryLower.includes('data') || categoryLower.includes('export')) {
    return 'from-indigo-500 to-indigo-600';
  }
  
  return 'from-gray-500 to-gray-600';
}

/**
 * Get icon and color mapping for a template
 */
export function getTemplateIconColor(
  id: string,
  category?: string,
  name?: string
): TemplateIconColorMapping {
  return {
    icon: getIconForTemplate(id, category, name),
    color: getColorForTemplate(id, category),
  };
}











